import React from 'react';
import {
  Sortable,
  SortableItemUIProps,
  SortableOnNavigateEvent
} from '@progress/kendo-react-sortable';
import { useToggle } from 'usehooks-ts';
import cloneDeep from 'lodash.clonedeep';

import {
  TimelineTextEditor,
  TimelineDurationEditor
} from '../../../components/dialog/reels/timeline';
import { useReels } from '../../../contexts/reels';
import { TextItem, TextOverlay } from './tabs/text/slides';

const SortableItemUI = (
  props: SortableItemUIProps & { isDragging: boolean }
) => {
  const { timeline, global_settings, handleChange } = useReels();
  const { style, attributes, dataItem, isDragging, forwardRef } = props;
  const [showTextEditor, setShowTextEditor] = useToggle(false);
  const [showDurationEditor, setShowDurationEditor] = useToggle(false);

  const textContainerAlign = React.useMemo(() => {
    if (global_settings.text_style.alignment === 'left') return 'start';
    if (global_settings.text_style.alignment === 'center') return 'center';
    if (global_settings.text_style.alignment === 'right') return 'end';
    return 'center';
  }, [global_settings.text_style.alignment]);

  return (
    <div
      style={style}
      {...attributes}
      ref={forwardRef}
      className={'timeline-item'}>
      {['image', 'video'].includes(dataItem.type) && (
        <div
          style={{
            width: '100%',
            height: '100%',
            position: 'relative'
          }}>
          <img
            alt={'failed to load image'}
            src={
              dataItem.type === 'image'
                ? dataItem.url
                : (dataItem.thumbnail ?? '')
            }
          />
          <div
            className={
              'd-flex align-items-center justify-content-center bg-white p-1 position-absolute'
            }
            style={{
              zIndex: 999,
              top: '8px',
              right: '8px',
              borderRadius: '4px'
            }}>
            <span
              className="material-symbols-outlined"
              style={{
                color: dataItem.type === 'video' ? '#14BF8B' : '#0a0a0a',
                fontSize: '16px'
              }}>
              {dataItem.type === 'video' ? 'smart_display' : 'image'}
            </span>
          </div>
          <div
            style={{
              position: 'absolute',
              inset: 0,
              width: '100%',
              height: '100%',
              display: 'flex',
              cursor: 'pointer',
              alignItems: 'center',
              justifyContent: textContainerAlign
            }}>
            <TextOverlay
              title={dataItem.content?.title}
              subtitle={dataItem.content?.subtitle}
              body={dataItem.content?.body}
            />
          </div>
        </div>
      )}

      {dataItem.type === 'text' && <TextItem item={dataItem} />}

      <div
        className={'timeline-item-overlay'}
        onClick={(e) => {
          if (isDragging) {
            e.stopPropagation();
            return;
          }

          const result = cloneDeep(timeline);
          const getId = result.findIndex(
            (timeline) => timeline.id === dataItem.id
          );
          if (getId !== -1) result.splice(getId, 1);
          handleChange({ timeline: result });
        }}>
        Remove
      </div>

      <div
        className={'button text'}
        onClick={setShowTextEditor}
        title={'Edit text'}>
        T
      </div>
      <div
        className={'button duration'}
        {...(dataItem.type !== 'video' && {
          onClick: () => setShowDurationEditor(),
          title: 'Edit duration'
        })}>
        {dataItem.duration_secs}s
      </div>

      {showTextEditor && (
        <TimelineTextEditor
          content={dataItem.content}
          onCancel={setShowTextEditor}
          onConfirm={(content) => {
            const result = cloneDeep(timeline);
            const getId = result.findIndex((r) => r.id === dataItem.id);
            if (getId !== -1) result[getId] = { ...dataItem, content };

            handleChange({ timeline: result });
            setShowTextEditor();
          }}
        />
      )}
      {showDurationEditor && (
        <TimelineDurationEditor
          isVideo={dataItem.type === 'video'}
          duration={dataItem.duration_secs}
          onCancel={setShowDurationEditor}
          onConfirm={(duration) => {
            const result = cloneDeep(timeline);
            const getId = result.findIndex((r) => r.id === dataItem.id);
            if (getId !== -1) result[getId] = { ...dataItem, duration };

            handleChange({ timeline: result });
            setShowDurationEditor();
          }}
        />
      )}
    </div>
  );
};

const empty = () => {
  return (
    <div
      className={'d-flex flex-column gap-2 text-center'}
      style={{
        color: '#003A64'
      }}>
      <span>Your video timeline is empty</span>
      <span
        style={{
          fontSize: '14px'
        }}>
        Click on media below to add them to the timeline
      </span>
    </div>
  );
};

export const Timeline: React.FC = () => {
  const [isDragging, setIsDragging] = React.useState(false);

  const {
    global_settings: { transition },
    timeline,
    handleChange
  } = useReels();

  const totalDuration = React.useMemo(() => {
    if (!timeline.length) return 0;

    // default duration is 2 secs
    // 1 sec fade on start video
    // 1 sec fade on end video
    const defaultDuration = 2;

    const itemDuration = timeline
      .map((timeline) => timeline.duration_secs ?? 0)
      .reduce((prev, current) => prev + current, 0);

    // if transition type is none (no transition) or timeline only has 1 item
    // transition duration is 0
    const transitionDuration =
      timeline.length === 1 || transition.type === 'none'
        ? 0
        : transition.duration_secs * (timeline.length - 1);

    return defaultDuration + itemDuration + transitionDuration;
  }, [timeline, transition.type, transition.duration_secs]);

  const onChangeShortable = (event: SortableOnNavigateEvent) => {
    handleChange({ timeline: event.newState as typeof timeline });
  };

  return (
    <div className={'d-flex flex-column gap-2'}>
      <div className={'d-flex justify-content-between'}>
        <span>
          Time:{' '}
          <span className={'text-danger'} style={{ fontWeight: 600 }}>
            {totalDuration} {totalDuration > 1 ? 'secs' : 'sec'}
          </span>
        </span>
        {timeline.length > 0 && (
          <span
            className={'cursor-pointer'}
            onClick={() => handleChange({ timeline: [] })}>
            Clear timeline
          </span>
        )}
      </div>

      <div className={'timeline-wrapper'}>
        <Sortable
          data={timeline}
          idField={'id'}
          disabledField={'disabled'}
          className={`timeline ${timeline.length > 0 ? '' : 'flex-column justify-content-center'} p-1`}
          emptyItemUI={empty}
          itemUI={(props) => (
            <SortableItemUI {...props} isDragging={isDragging} />
          )}
          onDragStart={() => setIsDragging(true)}
          onDragEnd={() => {
            setTimeout(() => setIsDragging(false), 50);
          }}
          onDragOver={onChangeShortable}
          onNavigate={onChangeShortable}
        />
      </div>
    </div>
  );
};
